/**
 * React Query Configuration
 * Centralized query client with REAL-TIME data sync
 * 
 * CRITICAL: staleTime = 0 ensures fresh data is ALWAYS fetched
 * This prevents stale data issues after mutations
 */
import { QueryClient } from '@tanstack/react-query';

// Create query client with real-time data priority
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // CRITICAL: Data is immediately stale - always fetch fresh
      staleTime: 0,
      // Keep unused data in cache for 2 minutes (for back navigation)
      gcTime: 2 * 60 * 1000,
      // CRITICAL: Always refetch when user tabs back to the app
      refetchOnWindowFocus: 'always',
      // Refetch when network reconnects
      refetchOnReconnect: 'always',
      // CRITICAL: Always refetch on mount to get latest data
      refetchOnMount: 'always',
      // Retry failed requests twice
      retry: 2,
      // Network mode: always try network first
      networkMode: 'always',
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
      // Network mode: always try network
      networkMode: 'always',
    },
  },
});

// Query keys for consistent cache management
export const queryKeys = {
  // Daily operations
  dailyOperations: (date, propertyId, locationId) => 
    ['daily-operations', date, propertyId || 'all', locationId || 'all'],
  dailyOperationsPaged: (date, tab, status, locationId) =>
    ['daily-operations', 'paged', date, tab, status || 'all', locationId || 'all'],
  
  // Dashboard
  dashboard: () => ['dashboard'],
  dashboardFull: () => ['dashboard', 'full'],
  
  // Bookings
  bookings: (filters) => ['bookings', filters],
  bookingDetail: (id) => ['booking', id],
  calendarBookings: (propertyId) => ['calendar', propertyId || 'all'],
  allBookings: () => ['bookings', 'all'],
  bookingsFeed: (filters) => ['bookings', 'feed', filters],
  bookingsInsights: (params) => ['bookings', 'insights', params],
  
  // Properties
  properties: (locationId) => ['properties', locationId || 'all'],
  property: (id) => ['property', id],
  
  // Locations
  locations: () => ['locations'],
  
  // Users
  users: (role) => ['users', role || 'all'],
  
  // Customers
  customers: (search) => ['customers', search || 'all'],
  
  // Tasks
  tasks: (filters) => ['tasks', filters],
  taskDetail: (id) => ['task', id],
  taskStats: () => ['tasks', 'stats'],
};

// Invalidation helpers - these immediately mark queries as stale and FORCE refetch
export const invalidateBookings = async () => {
  // CRITICAL: Use await to ensure invalidation completes before continuing
  // refetchType: 'all' forces refetch of ALL matching queries, not just active ones
  await queryClient.invalidateQueries({ queryKey: ['bookings'], refetchType: 'all' });
  await queryClient.invalidateQueries({ queryKey: ['calendar'], refetchType: 'all' });
  await queryClient.invalidateQueries({ queryKey: ['daily-operations'], refetchType: 'all' });
  await queryClient.invalidateQueries({ queryKey: ['dashboard'], refetchType: 'all' });
  await queryClient.invalidateQueries({ queryKey: ['booking'], refetchType: 'all' });
};

export const invalidateDailyOps = async () => {
  await queryClient.invalidateQueries({ queryKey: ['daily-operations'], refetchType: 'all' });
  await queryClient.invalidateQueries({ queryKey: ['dashboard'], refetchType: 'all' });
};

export const invalidateProperties = async () => {
  await queryClient.invalidateQueries({ queryKey: ['properties'], refetchType: 'all' });
  await queryClient.invalidateQueries({ queryKey: ['property'], refetchType: 'all' });
  await queryClient.invalidateQueries({ queryKey: ['dashboard'], refetchType: 'all' });
};

export const invalidateTasks = async () => {
  await queryClient.invalidateQueries({ queryKey: ['tasks'], refetchType: 'all' });
  await queryClient.invalidateQueries({ queryKey: ['task'], refetchType: 'all' });
};

export const invalidateUsers = async () => {
  await queryClient.invalidateQueries({ queryKey: ['users'], refetchType: 'all' });
};

export const invalidateCustomers = async () => {
  await queryClient.invalidateQueries({ queryKey: ['customers'], refetchType: 'all' });
};

export const invalidateLocations = async () => {
  await queryClient.invalidateQueries({ queryKey: ['locations'], refetchType: 'all' });
  await queryClient.invalidateQueries({ queryKey: ['properties'], refetchType: 'all' }); // Properties depend on locations
};

export const invalidateAll = async () => {
  await queryClient.invalidateQueries({ refetchType: 'all' });
};

// Force refetch - bypasses cache completely
export const forceRefetchAll = async () => {
  await queryClient.refetchQueries({ type: 'all', stale: true });
};

// Clear Service Worker API cache
export const clearServiceWorkerCache = async () => {
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames
          .filter(name => name.includes('api'))
          .map(name => caches.delete(name))
      );
      console.log('[QueryClient] Service Worker API cache cleared');
    } catch (e) {
      console.warn('[QueryClient] Failed to clear SW cache:', e);
    }
  }
};

// Get last updated timestamp for a query
export const getQueryUpdatedAt = (queryKey) => {
  const queryState = queryClient.getQueryState(queryKey);
  return queryState?.dataUpdatedAt;
};

// Check if any queries are fetching
export const isAnyQueryFetching = () => {
  return queryClient.isFetching() > 0;
};
